// rewardWallet.jsx
import { useState, useEffect } from "react";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";

export default function RewardWallet() {
  document.title = "Reward Wallet";

  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState(
    localStorage.getItem(SITENAMEALIAS + "_companyName") || "PayBito"
  );
  const [exchangeName, setExchangeName] = useState(
    localStorage.getItem("bill_bitcoins_exchange") || "Wallet"
  );
  
  // Reward wallet data
  const [cashbackAmount, setCashbackAmount] = useState("0.00");
  const [storedCredit, setStoredCredit] = useState("0.00");
  const [walletList, setWalletList] = useState([]);
  const [currency] = useState("USD");
  const [lastUpdated, setLastUpdated] = useState(null);

  // Get accessToken from URL
  const getAccessTokenFromUrl = () => {
    return new URLSearchParams(window.location.search).get("accessToken") || "";
  };

  // Favicon
  function applyFavicon(href) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = href;
  }

  function faviconFallback() {
    const logo = localStorage.getItem("bill_bitcoins_exchangeLogo");
    if (logo) applyFavicon(logo);
  }

  // Fetch broker info for favicon
  useEffect(() => {
    document.title = "Reward Wallet";
    const token = getAuthToken();
    const uuid = getAuthUuid();
    
    if (token && uuid) {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", `${WEBSERVICE}/merchant/getProfilePicture/${uuid}`, true);
      xhr.responseType = "blob";
      xhr.setRequestHeader("Authorization", `bearer ${token}`);
      xhr.setRequestHeader("UUID", uuid);
      xhr.onload = () =>
        xhr.status === 200 && xhr.response?.size > 0
          ? applyFavicon(URL.createObjectURL(xhr.response))
          : faviconFallback();
      xhr.onerror = faviconFallback;
      xhr.send();
    } else {
      faviconFallback();
    }

    // Get broker name
    const brokerId = localStorage.getItem("bill_bitcoins_broker_id");
    if (brokerId) {
      fetch(
        `https://accounts.paybito.com/api/home/getBrokerWiseExchangeInfo?brokerId=${brokerId}`
      )
        .then((r) => r.json())
        .then((res) => {
          const info = res.value?.[0] || {};
          const name = info.exchange || "";
          setExchangeName(name);
          localStorage.setItem("bill_bitcoins_exchange", name);
        })
        .catch(() => {});
    }
  }, []);

  // Fetch reward wallet data from API
  useEffect(() => {
    const fetchRewardData = async () => {
      setLoading(true);
      
      const accessToken = getAccessTokenFromUrl();

      if (!accessToken) {
        console.warn("No accessToken found in URL");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${WEBSERVICE}/campaigns/wallet?accessToken=${accessToken}`
        );

        const result = await response.json();
        
        if (result.status && result.data) {
          const data = result.data;
          setCashbackAmount(parseFloat(data.cashbackBalance || 0).toFixed(2));
          setStoredCredit(parseFloat(data.storeCreditBalance || 0).toFixed(2));
          setWalletList(Array.isArray(data.walletList) ? data.walletList : []);
          setLastUpdated(new Date().toLocaleString());
        } else {
          console.warn("Failed to fetch wallet data:", result.message);
        }
      } catch (error) {
        console.warn("Failed to fetch reward wallet data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRewardData();
  }, []);

  // Redirect to wallet
  const [walletLoading, setWalletLoading] = useState(false);
  
  function redirectToWallet() {
    if (walletLoading) return;
    setWalletLoading(true);
    
    const ALIAS = "bill_bitcoins";
    const accessToken = getAuthToken();
    const uuid = getAuthUuid();
    
    if (!accessToken || !uuid) {
      const domain = localStorage.getItem(`${ALIAS}_brokerDomain`);
      window.open(domain || `${window.location.origin}/my-wallet`, "_blank");
      setWalletLoading(false);
      return;
    }
    
    fetch(`${WEBSERVICE}/merchant/auto-login/exchange`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        UUID: uuid,
      },
      body: JSON.stringify({
        merchantId: localStorage.getItem(`${ALIAS}_merchant_id`),
        brokerId: localStorage.getItem(`${ALIAS}_broker_id`),
        uuid,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error?.error_data !== "0") {
          setWalletLoading(false);
          return;
        }
        return fetch("https://accounts.paybito.com/api/user/GetUserDetails", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${data.token}`,
          },
          body: JSON.stringify({ uuid: data.uuid, userId: data.userId }),
        })
          .then((r) => r.json())
          .then((d) => {
            const u = d.userResult;
            const params = new URLSearchParams({
              brokerId: u.brokerId,
              uuid: u.uuid,
              email: u.email,
              token: data.token,
              callFrom: "rewards",
            });
            window.open(
              `${window.location.origin}/autologin?${params}`,
              "_blank"
            );
            setWalletLoading(false);
          });
      })
      .catch(() => {
        setWalletLoading(false);
      });
  }

  const currencySymbols = {
    USD: "$", EUR: "€", GBP: "£", JPY: "¥", INR: "₹",
    AUD: "A$", CAD: "C$", CHF: "CHF", SGD: "S$",
  };
  
  const symbol = currencySymbols[currency] || "$";

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.loadingContainer}>
          <div style={S.spinnerLg} />
          <p style={S.loadingText}>Loading your reward wallet...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/icon?family=Material+Icons');
        * { box-sizing:border-box; font-family:'Roboto',sans-serif!important; }
        body { margin:0; }
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes scaleIn { from{transform:scaleX(0)} to{transform:scaleX(1)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
        .rw-card { background:#fff!important; }
        .rw-wallet-link { color:#284ccf!important; -webkit-text-fill-color:#284ccf!important; font-weight:600; text-decoration:none; cursor:pointer; }
        .rw-wallet-link:hover { text-decoration:underline; }
        @media(max-width:768px){
          .rw-balance-grid { flex-direction:column!important; }
        }
      `}</style>

      {/* Header */}
      <div style={S.header}>
        <div style={S.headerIcon}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 12V8H4v12h8" />
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="M12 12h.01" />
            <path d="M16 12h.01" />
            <path d="M20 12h.01" />
            <path d="M18 20a2 2 0 0 0 2-2" />
            <path d="M22 16a6 6 0 0 1-6 6" />
            <path d="M22 20a4 4 0 0 1-4 4" />
          </svg>
        </div>
        <h2 style={S.headerTitle}>
          Reward <span style={S.headerHighlight}>Wallet</span>
        </h2>
        <p style={S.headerSub}>Your cashback and stored credits</p>
      </div>

      {/* Card 1 — Balance Overview */}
      <div className="rw-card" style={S.card}>
        <div style={S.cardAccent} />
        <div style={S.cardBody}>
          <div className="rw-balance-grid" style={S.balanceGrid}>
            {/* Cashback Amount */}
            <div style={S.balanceCard}>
              <div style={S.balanceIcon}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#284ccf"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <p style={S.balanceLabel}>Cashback Amount</p>
              <p style={S.balanceValue}>
                {symbol}{cashbackAmount} <span style={S.balanceCurrency}>{currency}</span>
              </p>
              <p style={S.balanceDesc}>Earned from your transactions</p>
            </div>

            {/* Stored Credit */}
            <div style={S.balanceCard}>
              <div style={{ ...S.balanceIcon, background: "rgba(16,185,129,0.12)" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                  <path d="M7 15h.01M12 15h2" />
                </svg>
              </div>
              <p style={S.balanceLabel}>Stored Credit</p>
              <p style={{ ...S.balanceValue, color: "#10b981" }}>
                {symbol}{storedCredit} <span style={S.balanceCurrency}>{currency}</span>
              </p>
              <p style={S.balanceDesc}>Available for future purchases</p>
            </div>
          </div>
        </div>
      </div>

      {/* Card 2 — Store Credit Breakdown */}
      {walletList.length > 0 && (
        <div className="rw-card" style={S.card}>
          <div style={S.cardAccent} />
          <div style={S.cardBody}>
            <h4 style={S.sectionTitle}>Store Credit Breakdown</h4>
            <div style={S.breakdownList}>
              {walletList.map((item) => (
                <div key={item.merchantId} style={S.breakdownRow}>
                  <div style={S.breakdownLeft}>
                    <div style={S.breakdownIconWrap}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l1-4h16l1 4" />
                        <path d="M3 9h18v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9z" />
                        <path d="M9 21V9M15 21V9" />
                      </svg>
                    </div>
                    <div>
                      <p style={S.breakdownName}>{item.organizationName}</p>
                      <p style={S.breakdownId}>Merchant ID: {item.merchantId}</p>
                    </div>
                  </div>
                  <p style={S.breakdownAmount}>
                    {symbol}{parseFloat(item.totalBalance).toFixed(2)}
                    <span style={S.balanceCurrency}> {currency}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <p style={S.footer}>
        <span style={S.footerIcon}>🔒</span> Secured by {companyName}
      </p>
    </div>
  );
}

// Styles
const S = {
  page: {
    background: "linear-gradient(135deg,#e8ecf8 0%,#f0f2fa 50%,#eef0f7 100%)",
    minHeight: "100vh",
    fontFamily: "'Roboto',sans-serif",
    padding: "40px 20px 60px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
  },
  spinnerLg: {
    display: "inline-block",
    width: 36,
    height: 36,
    border: "3px solid rgba(40,76,207,.2)",
    borderTop: "3px solid #284ccf",
    borderRadius: "50%",
    animation: "spin .8s linear infinite",
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: "#6b7280",
  },
  header: {
    textAlign: "center",
    marginBottom: 32,
    animation: "fadeUp .5s ease",
  },
  headerIcon: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#284ccf,#102ab3)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
    boxShadow: "0 8px 24px rgba(40,76,207,.30)",
  },
  headerTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 4px",
  },
  headerHighlight: {
    background: "linear-gradient(135deg,#284ccf,#102ab3)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  headerSub: {
    fontSize: 14,
    color: "#6b7280",
    margin: 0,
  },
  card: {
    maxWidth: 720,
    margin: "0 auto 24px",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 8px 30px rgba(0,0,0,.08)",
    border: "1px solid rgba(40,76,207,.08)",
    overflow: "hidden",
    animation: "fadeUp .5s ease .1s both",
  },
  cardAccent: {
    height: 4,
    background: "linear-gradient(90deg,#284ccf,#102ab3,#10b981)",
    transformOrigin: "left",
    animation: "scaleIn .8s ease .3s both",
  },
  cardBody: {
    padding: 32,
    background: "#fff",
  },
  balanceGrid: {
    display: "flex",
    gap: 24,
  },
  balanceCard: {
    flex: 1,
    padding: 24,
    background: "#fafbff",
    borderRadius: 16,
    border: "1px solid rgba(40,76,207,.10)",
  },
  balanceIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "rgba(40,76,207,0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  balanceLabel: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: ".5px",
    color: "#6b7280",
    margin: "0 0 8px",
  },
  balanceValue: {
    fontSize: 32,
    fontWeight: 700,
    color: "#284ccf",
    margin: "0 0 4px",
    lineHeight: 1.2,
  },
  balanceCurrency: {
    fontSize: 14,
    fontWeight: 500,
    color: "#9ca3af",
  },
  balanceDesc: {
    fontSize: 12,
    color: "#9ca3af",
    margin: 0,
  },
  walletLinkContainer: {
    marginTop: 24,
    padding: "14px 18px",
    background: "rgba(40,76,207,0.04)",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: 14,
    color: "#374151",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 20px",
  },
  summaryGrid: {
    display: "flex",
    gap: 32,
    marginBottom: 24,
  },
  summaryItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  summaryIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "#f1f3f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  summaryContent: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  summaryLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: ".5px",
    color: "#9ca3af",
  },
  summaryValue: {
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
  },
  infoBox: {
    padding: "16px 18px",
    background: "rgba(40,76,207,0.04)",
    borderRadius: 12,
    border: "1px solid rgba(40,76,207,0.08)",
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  },
  infoText: {
    fontSize: 13,
    color: "#4b5563",
    lineHeight: 1.6,
  },
  breakdownList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  breakdownRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    background: "#fafbff",
    borderRadius: 12,
    border: "1px solid rgba(16,185,129,0.12)",
  },
  breakdownLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  breakdownIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "rgba(16,185,129,0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  breakdownName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    margin: "0 0 2px",
  },
  breakdownId: {
    fontSize: 11,
    color: "#9ca3af",
    margin: 0,
  },
  breakdownAmount: {
    fontSize: 18,
    fontWeight: 700,
    color: "#10b981",
    margin: 0,
    whiteSpace: "nowrap",
  },
  footer: {
    textAlign: "center",
    fontSize: 12,
    color: "#9ca3af",
    marginTop: 24,
    fontFamily: "'Roboto',sans-serif",
  },
  footerIcon: {
    marginRight: 6,
  },
};